import axios from 'axios'
import qs from 'qs'
import isEqual from 'lodash/isEqual'
import { formatRequest } from 'api'
import { mustLogin } from 'actions/auth'
import { currentUrl } from 'routing'
import { selectLoggedIn } from 'selectors/auth'
import authTypes from 'constants/authTypes'
import { typesArray } from 'lib/action'

type AxiosInstance = import('axios').AxiosInstance
type ApiAction = import('types/api').ApiAction
type ApiActionMeta = import('types/api').ApiActionMeta
type AxiosLikeError = import('types/api').AxiosLikeError
type ActionTypeDef = {
  REQUEST: string
  RECEIVE: string
  FAIL: string
}

const paramsSerializer = {
  serialize: (params: Record<string, unknown>) =>
    qs.stringify(params, {
      arrayFormat: 'repeat',
    }),
}

let authClient: AxiosInstance | undefined = undefined
let graphClient: AxiosInstance | undefined = undefined

export const getAuthClient = (recreate = false): AxiosInstance => {
  if (recreate || authClient === undefined) {
    authClient = axios.create({
      baseURL: '/api/auth',
      responseType: 'json',
      paramsSerializer,
    })
  }
  return authClient as AxiosInstance
}

export const getGraphClient = (recreate = false): AxiosInstance => {
  if (recreate || graphClient === undefined) {
    graphClient = axios.create({
      baseURL: '/api/graph',
      responseType: 'json',
      paramsSerializer,
      timeout: 5000,
    })
  }
  return graphClient as AxiosInstance
}

const skipNotLoggedIn = (getState: () => any, action: ApiAction, error?: AxiosLikeError) => {
  const { loggedIn } = selectLoggedIn(getState())
  const status = error?.status != null ? error.status : error?.response?.status
  if (loggedIn || status === 401) {
    return false
  }
  for (const typ of authTypes as ActionTypeDef[]) {
    if (isEqual(action.types, typesArray(typ))) {
      return false
    }
  }
  return true
}

const axiosMiddleware = (store: any) => (next: any) => (action: unknown) => {
  const act = action as ApiAction
  if (!act.meta?.request || !act.types) {
    return next(action)
  }

  const [REQUEST, RECEIVE, FAIL] = act.types
  const client = act.meta.client === 'auth' ? getAuthClient() : getGraphClient()
  const requestConfig = formatRequest(act)

  // Add auth header
  const state = store.getState() as { auth?: { jwt?: string } }
  const token = state.auth?.jwt
  const headers: Record<string, string> = {}
  if (token) {
    headers.Authorization = token
  }

  // Dispatch REQUEST
  next({ ...act, type: REQUEST })

  // Make async request, dispatch RECEIVE or FAIL
  return client.request({ ...requestConfig, headers }).then(
    (response: unknown) => {
      if (skipNotLoggedIn(store.getState, act)) {
        return
      }

      const nextAction: ApiAction = {
        type: RECEIVE,
        payload: response,
        meta: { previousAction: act } as ApiActionMeta,
      }
      next(nextAction)
      return nextAction
    },
    (error: unknown) => {
      let axiosError = error as AxiosLikeError

      // Handle 401 redirect
      if (axiosError.response?.status === 401) {
        store.dispatch(mustLogin({ returnUrl: currentUrl() }))
        window.location.href = '/login'
      }

      if (skipNotLoggedIn(store.getState, act, axiosError)) {
        return
      }

      // Normalize missing responses (network errors)
      if (!axiosError.response) {
        axiosError = {
          ...axiosError,
          response: {
            status: 0,
            data: axiosError.message,
          },
        }
      }

      const nextAction: ApiAction = {
        type: FAIL,
        error: axiosError,
        meta: { previousAction: act } as ApiActionMeta,
      }
      next(nextAction)
      return nextAction
    },
  )
}

export default axiosMiddleware